import { createRoot } from 'react-dom/client';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import App from './App';
import './index.css';
import { isLikelyBot } from '@utils/webglSupport';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

/**
 * Sentry init is deferred to idle time so it never competes with React
 * hydration for the main thread. The browser will run this after the
 * initial paint settles. `requestIdleCallback` falls back to `setTimeout`
 * on Safari, which is good enough — we only need to be off the critical
 * path, not strictly idle.
 */
function initSentryDeferred(): void {
  if (!SENTRY_DSN) return;
  void import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: import.meta.env.MODE,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      ignoreErrors: [
        /Error creating WebGL context/i,
        /WebGL: INVALID_OPERATION/i,
        /WebGL context lost/i,
      ],
      beforeSend(event) {
        if (isLikelyBot()) return null;
        return event;
      },
    });
  });
}

const ric: typeof requestIdleCallback | undefined =
  typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? window.requestIdleCallback.bind(window)
    : undefined;
if (ric) ric(initSentryDeferred, { timeout: 2000 });
else setTimeout(initSentryDeferred, 0);

const rootEl = document.getElementById('root');

if (!rootEl) {
  throw new Error('Root element #root not found in document');
}

/*
 * StrictMode is intentionally OFF.
 *
 * Framer-motion 11.18 + React 18 StrictMode throws inside
 * `createRendererMotionComponent` on the simulated-double mount
 * (useVisualElement re-initialization blows up). Re-enable once:
 *   a) framer-motion ships a StrictMode fix, or
 *   b) we migrate to `motion/react` from the new `motion` package.
 *
 * Effects / subscriptions are small enough today that losing the
 * double-invoke paranoia check is an acceptable short-term trade.
 */
createRoot(rootEl).render(
  <>
    <App />
    <Analytics />
    <SpeedInsights />
  </>,
);
